import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Icon, Modal, Typography } from '@ann/web-components';
import {
  ASSIGNEE_TYPES,
  PERMISSION_TYPES,
  SHARE_CONFIRMATION_COPY,
  SHARE_MODAL_COPY,
} from '../../constants';
import { useShareDashboard } from '../../hooks';
import { normalizeShareAssignment } from '../../utils/permissionsHelpers';
import { AssigneeRowItem, ShareConfirmation, UserDepartmentSearch } from './components';
import {
  AccessInfoContainer,
  AccessInfoDetails,
  AccessInfoHeader,
  AssigneesSection,
  DashboardNameRow,
  Divider,
  ExistingAccessList,
  MainFooter,
  ModalContent,
  ToBeAddedHeader,
} from './ShareDashboardModal.styled';

const ShareDashboardModal = ({
  open = false,
  onClose,
  onConfirm,
  dashboardId,
  dashboardName = 'Dashboard name',
  existingAccessCount = 0,
  existingSharedWith = [],
}) => {
  const [isAccessExpanded, setIsAccessExpanded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [existingAccessEntries, setExistingAccessEntries] = useState([]);

  const {
    addStagedSelection,
    assignments,
    commitStaged,
    confirmShare,
    onSearchChange,
    removeStagedSelection,
    removeAssignment,
    resetShareState,
    searchLoading,
    searchResults,
    searchTerm,
    stagedSelections,
    setShowConfirmation,
    showConfirmation,
    updatePermissions,
  } = useShareDashboard({ enabled: open });

  useEffect(() => {
    if (!open) return;
    setExistingAccessEntries(Array.isArray(existingSharedWith) ? existingSharedWith : []);
  }, [existingSharedWith, open]);

  const displayDashboardName = useMemo(() => {
    const trimmed = `${dashboardName || ''}`.trim();
    return trimmed || 'Dashboard name';
  }, [dashboardName]);

  const stagedCounts = useMemo(() => {
    return assignments.reduce(
      (acc, item) => {
        if (item?.type === ASSIGNEE_TYPES.USER) acc.users += 1;
        if (item?.type === ASSIGNEE_TYPES.DEPARTMENT) acc.departments += 1;
        return acc;
      },
      { users: 0, departments: 0 }
    );
  }, [assignments]);

  const accessCountLabel = useMemo(
    () => SHARE_MODAL_COPY.accessInfo.userCount(existingAccessEntries.length || existingAccessCount),
    [existingAccessCount, existingAccessEntries.length]
  );

  const handleAccessToggle = useCallback(() => {
    setIsAccessExpanded((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    resetShareState();
    setConfirming(false);
    onClose?.();
  }, [onClose, resetShareState]);

  const handleOpenConfirmation = useCallback(() => {
    if (assignments.length === 0) return;
    setShowConfirmation(true);
  }, [assignments.length, setShowConfirmation]);

  const handleConfirmationBack = useCallback(() => {
    setShowConfirmation(false);
  }, [setShowConfirmation]);

  const handleConfirmShare = useCallback(async () => {
    setConfirming(true);
    try {
      const result = await confirmShare(dashboardId);
      if (result.success) {
        const merged = [...existingAccessEntries, ...(result.sharedEntries || [])];
        onConfirm?.(merged);
        resetShareState();
        onClose?.();
      }
    } finally {
      setConfirming(false);
    }
  }, [confirmShare, dashboardId, existingAccessEntries, onClose, onConfirm, resetShareState]);

  const handleCopyLink = useCallback(async () => {
    const currentUrl = window?.location?.href;
    if (!currentUrl) return;
    try {
      await navigator.clipboard.writeText(currentUrl);
    } catch (error) {
      console.warn('Could not copy link', error);
    }
  }, []);

  const updateExistingPermissions = useCallback((id, payload) => {
    setExistingAccessEntries((prev) =>
      prev.map((entry) => {
        if (entry.id !== id) return entry;
        const normalized = normalizeShareAssignment({ ...entry, ...payload });
        const nextModes = Array.isArray(payload?.permissionModes)
          ? payload.permissionModes
          : Array.isArray(entry.permissionModes)
            ? entry.permissionModes
            : [normalized.permissionLevel];
        return {
          ...entry,
          permissionLevel: normalized.permissionLevel,
          permissionModes: nextModes,
          editPermissions: normalized.editPermissions,
          viewPermissions: normalized.viewPermissions,
        };
      })
    );
  }, []);

  const removeExistingAccess = useCallback((id) => {
    setExistingAccessEntries((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  return (
    <Modal isOpen={open} onClose={handleClose} size="medium" width="770px">
      <Modal.Header>
        <Modal.Header.Title>
          {showConfirmation ? SHARE_CONFIRMATION_COPY.title : SHARE_MODAL_COPY.title}
        </Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        {showConfirmation ? (
          <ShareConfirmation
            onBack={handleConfirmationBack}
            onConfirm={handleConfirmShare}
            assignments={assignments}
            dashboardName={displayDashboardName}
            confirming={confirming}
          />
        ) : (
          <ModalContent>
            <DashboardNameRow>
              <Icon name="grid" size="small" color="neutral" />
              <Typography category="body" size="medium" weight="regular" color="body">
                {displayDashboardName}
              </Typography>
            </DashboardNameRow>

            <UserDepartmentSearch
              searchTerm={searchTerm}
              onSearchChange={onSearchChange}
              searchLoading={searchLoading}
              searchResults={searchResults}
              onSelect={addStagedSelection}
              assignments={stagedSelections}
              onRemove={removeStagedSelection}
              onAdd={commitStaged}
            />

            {assignments.length > 0 && (
              <AssigneesSection>
                <ToBeAddedHeader>
                  <Typography category="subtitle" size="small" weight="semibold" color="subtitle">
                    {SHARE_MODAL_COPY.toBeAdded.title}
                  </Typography>
                  <Typography category="body" size="medium" weight="medium" color="body">
                    {SHARE_MODAL_COPY.toBeAdded.count(stagedCounts.users, stagedCounts.departments)}
                  </Typography>
                </ToBeAddedHeader>
                {assignments.map((item) => (
                  <AssigneeRowItem
                    key={item.id}
                    item={item}
                    variant="staged"
                    onPermissionChange={updatePermissions}
                    onRemove={removeAssignment}
                  />
                ))}
              </AssigneesSection>
            )}

            <Divider />

            <AccessInfoContainer>
              <AccessInfoHeader type="button" onClick={handleAccessToggle}>
                <AccessInfoDetails>
                  <Typography category="subtitle" size="small" weight="semibold">
                    {SHARE_MODAL_COPY.accessInfo.title}
                  </Typography>
                  <Typography category="body" size="medium" weight="medium">
                    {accessCountLabel}
                  </Typography>
                </AccessInfoDetails>
                <Icon
                  name={isAccessExpanded ? 'chevronUp' : 'chevronDown'}
                  size="medium"
                  color="neutral"
                />
              </AccessInfoHeader>
              {isAccessExpanded && existingAccessEntries.length > 0 && (
                <ExistingAccessList>
                  {existingAccessEntries.map((item) => (
                    <AssigneeRowItem
                      key={`existing-${item.id}`}
                      item={item}
                      variant="existing"
                      onPermissionChange={updateExistingPermissions}
                      onRemove={removeExistingAccess}
                    />
                  ))}
                </ExistingAccessList>
              )}
            </AccessInfoContainer>

            <MainFooter>
              <Button
                type="secondary"
                size="medium"
                label={SHARE_MODAL_COPY.copyLinkButton}
                onClick={handleCopyLink}
              />
              <Button
                type="primary"
                size="medium"
                label={SHARE_MODAL_COPY.shareButton}
                disabled={assignments.length === 0}
                onClick={handleOpenConfirmation}
              />
            </MainFooter>
          </ModalContent>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default memo(ShareDashboardModal);
